import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ShoppingBag, Calendar, Loader2, Printer, Share2, FileText,
  Search, Eye, Wallet, RefreshCw, Package,
} from "lucide-react";
import { toast } from "sonner";
import { smartMatch } from "../../utils/arabicSearch";
import { InvoiceDetailModal } from "./PurchaseTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const splitDate = (iso) => {
  if (!iso) return { day: "—", time: "" };
  try {
    const d = new Date(iso);
    return {
      day: d.toLocaleDateString("en-GB", { year: "2-digit", month: "2-digit", day: "2-digit" }),
      time: d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    };
  } catch { return { day: iso, time: "" }; }
};

const SUPPORT_PHONES = [
  { local: "07717637525", label: "خدمة العملاء" },
  { local: "07842220088", label: "الدعم الفني" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const PurchasesReportTab = () => {
  const [from, setFrom] = useState(daysAgoISO(6));
  const [to, setTo] = useState(todayISO());
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openInvId, setOpenInvId] = useState(null);
  const [products, setProducts] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/reports/purchases`, {
        params: { date_from: from, date_to: to, q },
      });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تحميل التقرير");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [from, to]);
  useEffect(() => {
    const t = setTimeout(() => { fetchData(); }, 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  // Load products once for the InvoiceDetailModal (needed for pre-fill A+/B+ prices).
  useEffect(() => {
    (async () => {
      try {
        const { data: prods } = await axios.get(`${API}/admin/products`);
        setProducts(prods || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  // Server does the filtering when `q` is set (bypasses date filter); no
  // additional client-side filter needed.
  const filtered = data?.invoices || [];

  const s = data?.summary || {};

  const buildPrintHtml = () => {
    const dateStr = new Date().toLocaleString("ar-IQ", { dateStyle: "medium", timeStyle: "short" });
    const rows = filtered.map((r, i) => {
      const dt = splitDate(r.created_at);
      const payCls = r.payment_status === "paid" ? "pay-green"
                    : r.payment_status === "partial" ? "pay-orange" : "pay-red";
      const payLbl = r.payment_status === "paid" ? "مسدَّدة"
                    : r.payment_status === "partial" ? "جزئي" : "غير مسدَّدة";
      return `<tr>
        <td class="c" dir="ltr">${i + 1}</td>
        <td class="c" dir="ltr"><b>#${r.invoice_number || "—"}</b></td>
        <td class="c" dir="ltr">
          <div><b>${dt.day}</b></div>
          <div style="font-size:9px;color:#5C6E64">${dt.time}</div>
        </td>
        <td><b>${(r.supplier_name || "—").replace(/</g, "&lt;")}</b>
          ${r.supplier_type ? `<div style="font-size:9px;color:#5C6E64">${r.supplier_type.replace(/</g,"&lt;")}</div>` : ""}
        </td>
        <td class="c" dir="ltr">${r.items_count}</td>
        <td class="c" dir="ltr">${fmt(r.total_quantity)}</td>
        <td class="c" dir="ltr"><b>${fmt(r.total)}</b></td>
        <td class="c amt-green" dir="ltr">${fmt(r.paid)}</td>
        <td class="c amt-red" dir="ltr">${fmt(r.remaining)}</td>
        <td class="c"><span class="pill ${payCls}">${payLbl}</span></td>
      </tr>`;
    }).join("");
    const supportHtml = SUPPORT_PHONES.map((p) => `${p.label}: <b dir="ltr">${p.local}</b>`).join(" · ");

    return `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"/>
      <title>تقرير مشتريات القوائم — جملة أون لاين</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: 'Tajawal','Tahoma','Segoe UI',Arial,sans-serif; padding: 12px; color: #111; direction: rtl; margin: 0; }
        .header { display: flex; justify-content: space-between; align-items: flex-start;
          padding-bottom: 12px; border-bottom: 3px solid #F5A623; margin-bottom: 12px; }
        .brand h1 { color: #E85D45; margin: 0; font-size: 22px; }
        .brand p { margin: 4px 0 0; color: #5C6E64; font-size: 12px; }
        .date-box { text-align: left; font-size: 11px; color: #5C6E64;
          background: #F8FAFC; border: 1px solid #E4EBE6; border-radius: 10px; padding: 8px 12px; }
        .date-box b { display: block; color: #1E3A5F; font-size: 13px; }
        .filter-line { font-size: 11px; color: #5C6E64; margin-bottom: 8px; }
        .filter-line b { color: #1E3A5F; }
        table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
        thead th { background: #F5A623; color: #fff; padding: 6px 4px; text-align: right; }
        thead th.c { text-align: center; }
        tbody td { border: 1px solid #E4EBE6; padding: 5px; vertical-align: middle; }
        tbody td.c { text-align: center; }
        tbody tr:nth-child(even) td { background: #FFFBEB; }
        .amt-red { color: #B91C1C; font-weight: 900; }
        .amt-green { color: #15803D; font-weight: 900; }
        .pill { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 800; }
        .pay-green { background: #DCFCE7; color: #15803D; }
        .pay-orange { background: #FEF3C7; color: #B45309; }
        .pay-red { background: #FEE2E2; color: #B91C1C; }
        .summary-blocks { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; margin-top: 12px; }
        .block { border-radius: 12px; padding: 12px 14px; color: #fff; text-align: center; }
        .block .lbl { font-size: 11px; opacity: 0.9; }
        .block .val { font-size: 22px; font-weight: 900; margin-top: 2px; }
        .b-orange { background: linear-gradient(90deg,#E85D45,#F5A623); }
        .b-green { background: linear-gradient(90deg,#15803D,#22C55E); }
        .b-red { background: linear-gradient(90deg,#B91C1C,#DC2626); }
        .footer { margin-top: 20px; padding-top: 12px; border-top: 2px dashed #E4EBE6;
          text-align: center; font-size: 11px; color: #5C6E64; line-height: 1.7; }
        .footer .brand-line { color: #E85D45; font-weight: 900; font-size: 14px; }
      </style></head>
      <body>
        <div class="header">
          <div class="brand">
            <h1>جملة أون لاين</h1>
            <p>تقرير مشتريات القوائم · Jumlaty Online</p>
          </div>
          <div class="date-box">
            <b>تاريخ الطباعة</b>
            <span dir="ltr">${dateStr}</span>
          </div>
        </div>
        <div class="filter-line">
          الفترة: <b>${from}</b> إلى <b>${to}</b> · عدد قوائم الشراء: <b>${fmt(filtered.length)}</b>
        </div>
        <table>
          <thead>
            <tr>
              <th class="c" style="width:30px">#</th>
              <th class="c" style="width:60px">رقم القائمة</th>
              <th class="c" style="width:75px">التاريخ</th>
              <th>المُجهّز</th>
              <th class="c" style="width:50px">أصناف</th>
              <th class="c" style="width:70px">إجمالي الكمية</th>
              <th class="c" style="width:100px">إجمالي المبلغ</th>
              <th class="c" style="width:90px">مدفوع</th>
              <th class="c" style="width:90px">متبقي</th>
              <th class="c" style="width:80px">الحالة</th>
            </tr>
          </thead>
          <tbody>
            ${rows || `<tr><td colspan="10" class="c" style="padding:20px;color:#5C6E64">لا توجد قوائم شراء</td></tr>`}
          </tbody>
        </table>
        <div class="summary-blocks">
          <div class="block b-orange">
            <div class="lbl">🛒 إجمالي المشتريات</div>
            <div class="val" dir="ltr">${fmt(s.total_spent)} د.ع</div>
          </div>
          <div class="block b-green">
            <div class="lbl">✅ المدفوع للمجهّزين</div>
            <div class="val" dir="ltr">${fmt(s.total_paid)} د.ع</div>
          </div>
          <div class="block b-red">
            <div class="lbl">🔴 المتبقي علينا</div>
            <div class="val" dir="ltr">${fmt(s.total_remaining)} د.ع</div>
          </div>
        </div>
        <div class="footer">
          <div class="brand-line">جملة أون لاين — Jumlaty Online</div>
          <div>${supportHtml}</div>
          <div>الموقع: jumlaty.online</div>
        </div>
        <script>window.onload=()=>setTimeout(()=>window.print(),300);</script>
      </body></html>`;
  };

  const handlePrint = () => {
    if (!filtered.length) { toast.error("لا توجد بيانات"); return; }
    const w = window.open("", "_blank", "width=1100,height=980");
    if (!w) { toast.error("فعّل النوافذ المنبثقة"); return; }
    w.document.open(); w.document.write(buildPrintHtml()); w.document.close(); w.focus();
  };

  const handleWhatsApp = () => {
    if (!filtered.length) { toast.error("لا توجد بيانات"); return; }
    const lines = [];
    lines.push("*🛒 تقرير مشتريات القوائم — جملة أون لاين*");
    lines.push(`الفترة: ${from} → ${to}`);
    lines.push(`عدد القوائم: *${fmt(filtered.length)}*`);
    lines.push("─".repeat(22));
    filtered.slice(0, 40).forEach((r, i) => {
      const status = r.payment_status === "paid" ? "✅" : r.payment_status === "partial" ? "🟠" : "🔴";
      lines.push(`${i + 1}. #${r.invoice_number} · ${r.supplier_name}`);
      lines.push(`   ${status} ${fmt(r.total)} د.ع · مدفوع ${fmt(r.paid)} · متبقي ${fmt(r.remaining)}`);
    });
    if (filtered.length > 40) lines.push(`... و ${filtered.length - 40} قائمة أخرى`);
    lines.push("─".repeat(22));
    lines.push(`*🛒 إجمالي المشتريات: ${fmt(s.total_spent)} د.ع*`);
    lines.push(`*✅ المدفوع: ${fmt(s.total_paid)} د.ع*`);
    lines.push(`*🔴 المتبقي: ${fmt(s.total_remaining)} د.ع*`);
    lines.push("");
    lines.push("*جملة أون لاين — Jumlaty Online*");
    SUPPORT_PHONES.forEach((p) => lines.push(`${p.label}: ${p.local}`));
    const url = `https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-3" dir="rtl" data-testid="purchases-report-tab">
      {/* Header */}
      <div className="bg-gradient-to-l from-[#E85D45] to-[#F5A623] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <ShoppingBag className="w-5 h-5" />
          <h3 className="font-bold text-lg">تقارير مشتريات القوائم</h3>
        </div>
        <p className="text-xs opacity-95">
          كل قوائم الشراء من المجهّزين بالتاريخ — مع مسدَّد / غير مسدَّد وقابل للتعديل بالضغط على القائمة.
        </p>
      </div>

      {/* Date range */}
      <div className="bg-white border-2 border-[#E4EBE6] rounded-2xl p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[140px]">
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5A623]" />
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              data-testid="purch-from"
              className="w-full pr-9 pl-2 py-2 rounded-xl border-2 border-[#F5A623]/50 bg-white text-sm" />
          </div>
          <span className="text-[#5C6E64] font-bold">→</span>
          <div className="relative flex-1 min-w-[140px]">
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5A623]" />
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              data-testid="purch-to"
              className="w-full pr-9 pl-2 py-2 rounded-xl border-2 border-[#F5A623]/50 bg-white text-sm" />
          </div>
          <button onClick={fetchData} data-testid="purch-refresh"
            className="bg-[#F5A623] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> تحديث
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 text-[10px]">
          <QuickBtn label="اليوم" onClick={() => { setFrom(todayISO()); setTo(todayISO()); }} />
          <QuickBtn label="أمس" onClick={() => { const d = daysAgoISO(1); setFrom(d); setTo(d); }} />
          <QuickBtn label="آخر ٧ أيام" onClick={() => { setFrom(daysAgoISO(6)); setTo(todayISO()); }} />
          <QuickBtn label="آخر ٣٠ يوم" onClick={() => { setFrom(daysAgoISO(29)); setTo(todayISO()); }} />
        </div>
      </div>

      {/* Search + export — ALWAYS visible so admin can find any old purchase invoice */}
      <div className="bg-white border-2 border-[#E4EBE6] rounded-2xl p-3 flex flex-wrap gap-2 justify-between items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5A623]" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="purch-search"
            placeholder="🔍 بحث سريع: رقم قائمة / اسم مُجهّز — يبحث بكل القوائم حتى القديمة"
            className="w-full pr-9 pl-3 py-2.5 rounded-xl border-2 border-[#F5A623]/50 focus:border-[#F5A623] bg-white text-sm font-bold focus:outline-none" />
          {q && (
            <button onClick={() => setQ("")} data-testid="purch-search-clear"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
              <span className="text-slate-500 text-xs">✕</span>
            </button>
          )}
        </div>
        {filtered.length > 0 && (
          <div className="flex gap-2">
            <button onClick={handlePrint} data-testid="purch-print"
              className="bg-[#1E3A5F] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1.5">
              <Printer className="w-3.5 h-3.5" /> طباعة
            </button>
            <button onClick={handlePrint} data-testid="purch-pdf"
              className="bg-[#B91C1C] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" /> PDF
            </button>
            <button onClick={handleWhatsApp} data-testid="purch-whatsapp"
              className="bg-[#25D366] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1.5">
              <Share2 className="w-3.5 h-3.5" /> واتساب
            </button>
          </div>
        )}
      </div>

      {/* Search-mode banner */}
      {q && !loading && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-900 flex items-center gap-2" data-testid="purch-search-mode">
          <Search className="w-3.5 h-3.5" />
          <span>وضع البحث السريع — يشمل كل قوائم الشراء مو محدد بالتاريخ. نتائج: <b>{filtered.length}</b> قائمة</span>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin inline text-[#F5A623]" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-[#E4EBE6] rounded-2xl p-8 text-center text-[#5C6E64]">
          <Package className="w-10 h-10 mx-auto opacity-30 mb-2" />
          <p className="font-bold">لا توجد قوائم شراء في هذه الفترة</p>
        </div>
      ) : (
        <div className="bg-white border-2 border-[#E4EBE6] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="purch-table">
              <thead className="bg-[#F5A623] text-white">
                <tr>
                  <th className="p-2 text-center">#</th>
                  <th className="p-2 text-center">رقم</th>
                  <th className="p-2 text-center">التاريخ</th>
                  <th className="p-2 text-right">المُجهّز</th>
                  <th className="p-2 text-center">أصناف</th>
                  <th className="p-2 text-center">الكمية</th>
                  <th className="p-2 text-center">الإجمالي</th>
                  <th className="p-2 text-center">مدفوع</th>
                  <th className="p-2 text-center">متبقي</th>
                  <th className="p-2 text-center">الحالة</th>
                  <th className="p-2 text-center print:hidden"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <PurchRow key={r.id} r={r} i={i} onView={() => setOpenInvId(r.id)} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary blocks */}
      {data && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-gradient-to-l from-[#E85D45] to-[#F5A623] text-white rounded-2xl p-5 shadow-lg" data-testid="purch-block-total">
            <div className="flex items-center gap-2 mb-1"><ShoppingBag className="w-5 h-5" /><p className="font-bold text-sm">إجمالي المشتريات</p></div>
            <p className="text-3xl font-black" dir="ltr">{fmt(s.total_spent)} د.ع</p>
            <p className="text-[10px] opacity-90 mt-1"><span dir="ltr">{fmt(data.count)}</span> قائمة شراء</p>
          </div>
          <div className="bg-gradient-to-l from-[#15803D] to-[#22C55E] text-white rounded-2xl p-5 shadow-lg" data-testid="purch-block-paid">
            <div className="flex items-center gap-2 mb-1"><Wallet className="w-5 h-5" /><p className="font-bold text-sm">المدفوع للمجهّزين</p></div>
            <p className="text-3xl font-black" dir="ltr">{fmt(s.total_paid)} د.ع</p>
            <p className="text-[10px] opacity-90 mt-1"><span dir="ltr">{fmt(s.paid_count)}</span> مسدَّدة كاملاً · <span dir="ltr">{fmt(s.partial_count)}</span> جزئي</p>
          </div>
          <div className="bg-gradient-to-l from-[#B91C1C] to-[#DC2626] text-white rounded-2xl p-5 shadow-lg" data-testid="purch-block-remaining">
            <div className="flex items-center gap-2 mb-1"><span className="text-lg">🔴</span><p className="font-bold text-sm">المتبقي علينا</p></div>
            <p className="text-3xl font-black" dir="ltr">{fmt(s.total_remaining)} د.ع</p>
            <p className="text-[10px] opacity-90 mt-1"><span dir="ltr">{fmt(s.unpaid_count)}</span> قائمة غير مسدَّدة</p>
          </div>
        </div>
      )}

      {/* Purchase invoice detail modal */}
      {openInvId && (
        <InvoiceDetailModal
          invoiceId={openInvId}
          products={products}
          onClose={() => setOpenInvId(null)}
          onDeleted={() => { setOpenInvId(null); fetchData(); }}
        />
      )}
    </div>
  );
};

const QuickBtn = ({ label, onClick }) => (
  <button onClick={onClick} className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] rounded-full px-2 py-1 font-bold">
    {label}
  </button>
);

const PurchRow = ({ r, i, onView }) => {
  const dt = splitDate(r.created_at);
  const payBadge =
    r.payment_status === "paid" ? "bg-[#DCFCE7] text-[#15803D]" :
    r.payment_status === "partial" ? "bg-[#FEF3C7] text-[#B45309]" :
    "bg-[#FEE2E2] text-[#B91C1C]";
  const payLbl =
    r.payment_status === "paid" ? "✅ مسدَّدة" :
    r.payment_status === "partial" ? "🟠 جزئي" :
    "🔴 غير مسدَّدة";
  return (
    <tr className={`${i % 2 === 0 ? "bg-white" : "bg-[#FFFBEB]"} border-b border-[#E4EBE6] hover:brightness-95`} data-testid={`purch-row-${r.id}`}>
      <td className="p-1.5 text-center text-[#5C6E64]" dir="ltr">{i + 1}</td>
      <td className="p-1.5 text-center font-black text-[#a06e10]" dir="ltr">#{r.invoice_number || "—"}</td>
      <td className="p-1.5 text-center" dir="ltr">
        <div className="font-bold text-[11px]">{dt.day}</div>
        <div className="text-[9px] text-[#5C6E64]">{dt.time}</div>
      </td>
      <td className="p-1.5">
        <div className="font-bold text-[#111814]">{r.supplier_name || "—"}</div>
        {r.supplier_type && <div className="text-[10px] text-[#5C6E64]">{r.supplier_type}</div>}
      </td>
      <td className="p-1.5 text-center font-bold" dir="ltr">{r.items_count}</td>
      <td className="p-1.5 text-center font-bold" dir="ltr">{fmt(r.total_quantity)}</td>
      <td className="p-1.5 text-center font-black text-[#a06e10]" dir="ltr">{fmt(r.total)}</td>
      <td className="p-1.5 text-center font-bold text-[#15803D]" dir="ltr">{fmt(r.paid)}</td>
      <td className="p-1.5 text-center font-bold text-[#B91C1C]" dir="ltr">{fmt(r.remaining)}</td>
      <td className="p-1.5 text-center">
        <span className={`text-[10px] rounded-full px-2 py-0.5 font-bold whitespace-nowrap ${payBadge}`}>{payLbl}</span>
      </td>
      <td className="p-1.5 text-center print:hidden">
        <button onClick={onView} data-testid={`purch-view-${r.id}`}
          className="bg-[#F5A623] hover:bg-[#dc9320] text-white rounded-lg p-1.5" title="عرض القائمة">
          <Eye className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
};

export default PurchasesReportTab;
